import React, { useState, useContext, useEffect } from 'react';
import { profileData, skillsData } from '../mock';
import { ThemeContext } from './ThemeProvider';
import { User, Github, Linkedin, Mail, MapPin, Calendar, Code, Star } from 'lucide-react';
import './ProfileCard.css';
import {toast, Toaster} from 'react-hot-toast';

const ProfileCard = ({ isExpanded, onToggle }) => {
  const { currentTheme } = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState('info');

  // State to store dynamic stats
  const [githubRepos, setGithubRepos] = useState(profileData.githubRepos || 0);
  const [profileViews, setProfileViews] = useState(profileData.totalViews || 0);

  // const handleCopyEmail = () => {
  //   navigator.clipboard.writeText(profileData.email);
  //   // Could add toast notification here
  // };

const handleCopyEmail = () => {
  navigator.clipboard.writeText(profileData.email)
    .then(() => {
      toast.success('📧 Email copied to clipboard!');
    })
    .catch(() => {
      toast.error('❌ Failed to copy email.');
    });
};



  const generateQRCode = () => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(profileData.linkedin)}`;
  };

  const fetchGitHubStats = async () => {
    try {
      const res = await fetch("https://api.github.com/users/fireblaster000/repos");
      const repos = await res.json();
      setGithubRepos(repos.length);
    } catch (err) {
      console.error("GitHub API Error:", err);
    }
  };

  // Fetch profile views from Counter API
  const fetchProfileViews = async () => {
    try {
      const apiUrl = `https://api.counterapi.dev/v2/${process.env.REACT_APP_COUNTER_WORKSPACE}/${process.env.REACT_APP_COUNTER_NAME}/up?apikey=${process.env.REACT_APP_COUNTER_API_KEY}`;
      const res = await fetch(apiUrl);
      if (!res.ok) throw new Error(`Counter API returned status ${res.status}`);
      const data = await res.json();
      setProfileViews(data.data.up_count);
    } catch (err) {
      console.error("Counter API Error:", err);
    }
  };

  useEffect(() => {
    fetchGitHubStats();
    fetchProfileViews();
  }, []);

  return (
    <>
    <Toaster position="top-right" />
    <div className={`profile-card ${isExpanded ? 'expanded' : 'mini'} theme-${currentTheme}`}>
      <div className="card-glow-effect" />
      
      {/* Mini Profile View */}
      <div className="mini-view" onClick={onToggle}>
        <div className="mini-avatar">
          <img src={profileData.photo} alt={profileData.name} />
          <div className="status-indicator online" />
        </div>
        <div className="mini-info">
          <h3 className="mini-name">{profileData.name.split(' ')[0]}</h3>
          <p className="mini-status">ONLINE</p>
          <div className="mini-stats">
            <div className="stat-item">
              <Github size={12} />
              <span>{githubRepos}</span>
            </div>
          </div>
        </div>
        <div className="expand-arrow">
          {isExpanded ? '←' : '→'}
        </div>
      </div>

      {/* Expanded Profile View */}
      <div className="expanded-view">
        <div className="profile-header">
          <div className="avatar-container">
            <img src={profileData.photo} alt={profileData.name} />
            <div className="avatar-ring" />
            <div className="status-badge">
              <div className="status-dot" />
              Available for hire
            </div>
          </div>
          
          <div className="profile-info">
            <h2 className="profile-name">{profileData.name}</h2>
            <p className="profile-tagline">{profileData.tagline}</p>
            <div className="profile-location">
              <MapPin size={14} />
              {profileData.location}
            </div>
          </div>

          <button className="close-btn" onClick={onToggle}>×</button>
        </div>

        {/* Navigation Tabs */}
        <div className="profile-nav">
          <button 
            className={`nav-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <User size={16} />
            Info
          </button>
          <button 
            className={`nav-tab ${activeTab === 'skills' ? 'active' : ''}`}
            onClick={() => setActiveTab('skills')}
          >
            <Code size={16} />
            Skills
          </button>
          <button 
            className={`nav-tab ${activeTab === 'connect' ? 'active' : ''}`}
            onClick={() => setActiveTab('connect')}
          >
            <Mail size={16} />
            Connect
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'info' && (
            <div className="info-tab">
              <div className="github-stats">
                <h4>GitHub Activity</h4>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Calendar size={16} />
                    </div>
                    <div className="stat-details">
                      <span className="stat-value">{githubRepos}</span>
                      <span className="stat-label">Total Repos</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Star size={16} />
                    </div>
                    <div className="stat-details">
                      <span className="stat-value">{profileViews}</span>
                      <span className="stat-label">Total Profile Views</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="quick-info">
                <h4>Current Status</h4>
                <p>{profileData.currentStatus}</p>
              </div>
            </div>
          )}

          {activeTab === 'skills' && (
            <div className="skills-tab">
              {Object.entries(skillsData).map(([category, skills]) => (
                <div key={category} className="skill-category">
                  <h4>{category}</h4>
                  <div className="skill-tags">
                    {skills.map((skill) => (
                      <span key={skill} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'connect' && (
            <div className="connect-tab">
              <div className="connect-options">
                <button 
                  className="connect-btn email"
                  onClick={handleCopyEmail}
                >
                  <Mail size={16} />
                  Copy Email
                </button>
                
                <a 
                  href={profileData.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="connect-btn github"
                >
                  <Github size={16} />
                  GitHub
                </a>
                
                <a 
                  href={profileData.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="connect-btn linkedin"
                >
                  <Linkedin size={16} />
                  LinkedIn
                </a>
              </div>

              <div className="qr-section">
                <h4>Quick Connect</h4>
                <div className="qr-code">
                  <img 
                    src={generateQRCode()}
                    alt="LinkedIn QR Code"
                  />
                  <p>Scan for LinkedIn</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ProfileCard;